import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, ArrowRight, MapPin, CalendarDays, Clock } from "lucide-react";

interface FeedItem {
  item_key: string;
  kind: "playdate" | "going";
  ref_id: string;
  title: string | null;
  item_date: string;
  start_time: string | null;
  type: string;
  venue_name?: string | null;
  venue_image?: string | null;
  activity_id?: string | null;
  activity_slug?: string | null;
  activity_name?: string | null;
  activity_image?: string | null;
  organiser_first_name?: string | null;
  organiser_avatar_url?: string | null;
}

const MAX_ITEMS = 10;

const formatDateLabel = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const formatTimeLabel = (t: string | null) => {
  if (!t) return null;
  const [h, m] = t.split(":");
  return `${h}:${m}`;
};

export const MeetLocalsSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [feedRes, countRes] = await Promise.all([
          supabase.rpc("get_upcoming_meet_feed_public" as any, { limit_count: MAX_ITEMS }),
          supabase.rpc("get_registered_users_count"),
        ]);

        if (cancelled) return;

        setItems(((feedRes.data as any[]) || []) as FeedItem[]);
        if (typeof countRes.data === "number") setMemberCount(countRes.data);
      } catch (err) {
        console.error("MeetLocalsSection error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const goToItem = (item: FeedItem) => {
    if (item.kind === "playdate") {
      const path = `/play-date/${item.ref_id}`;
      navigate(user ? path : `/auth?returnUrl=${encodeURIComponent(path)}`);
    } else {
      const path = `/activity/${item.activity_slug || item.activity_id}`;
      navigate(path);
    }
  };

  if (!loading && items.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-bl from-primary/5 via-background to-light-fuschia/40 border-t border-border/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Users className="h-5 w-5 text-primary" />
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                Meet parents and carers nearby
              </h2>
              {memberCount !== null && memberCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold">
                  {memberCount.toLocaleString("en-GB")} members
                </span>
              )}
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              Upcoming playdates and activities people are going to
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-64 w-[280px] rounded-lg shrink-0" />
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="overflow-x-auto snap-x snap-mandatory scrollbar-none scroll-px-0">
            <div className="flex gap-3 pb-2">
              {items.map((p) => {
                const isGoing = p.kind === "going";
                const heading =
                  p.title ||
                  p.activity_name ||
                  (p.type === "meetup" ? "Meetup" : isGoing ? "Activity" : "Playdate");
                const location = p.venue_name || (isGoing ? null : p.activity_name);
                const time = formatTimeLabel(p.start_time);
                const heroImage = p.venue_image || p.activity_image;
                const badgeLabel = isGoing
                  ? "Casual Playdate"
                  : p.type === "private"
                    ? "Private"
                    : "Meet up";
                const badgeClass = isGoing
                  ? "bg-primary/85 text-primary-foreground"
                  : "bg-black/55 text-white";
                const subtitle = !user
                  ? isGoing
                    ? "Someone's going"
                    : "Open invite"
                  : p.organiser_first_name
                    ? isGoing
                      ? `${p.organiser_first_name} is going`
                      : `${p.organiser_first_name} invites you`
                    : isGoing
                      ? "Someone's going"
                      : "Open invite";
                return (
                  <button
                    key={p.item_key}
                    onClick={() => goToItem(p)}
                    className="group shrink-0 w-[280px] text-left bg-card rounded-lg overflow-hidden border border-border hover:shadow-md hover:border-primary/40 transition-all snap-start flex flex-col"
                  >
                    <div className="relative w-full h-36 bg-muted overflow-hidden">
                      {heroImage ? (
                        <img
                          src={heroImage}
                          alt={location || heading}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 via-primary/10 to-accent/20 flex items-center justify-center">
                          <Users className="h-10 w-10 text-primary/40" />
                        </div>
                      )}
                      <span
                        className={`absolute top-2 left-2 inline-flex items-center justify-center text-center text-[10px] font-semibold tracking-wide uppercase leading-none px-2 py-1 rounded backdrop-blur-sm whitespace-nowrap ${badgeClass}`}
                      >
                        {badgeLabel}
                      </span>
                    </div>
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="h-7 w-7">
                          {user && p.organiser_avatar_url ? (
                            <AvatarImage
                              src={p.organiser_avatar_url}
                              alt={p.organiser_first_name || ""}
                            />
                          ) : null}
                          <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                            {user ? (p.organiser_first_name || "?").charAt(0) : "?"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs text-muted-foreground truncate">
                          {subtitle}
                        </span>
                      </div>
                      <h3 className="font-semibold text-sm text-foreground line-clamp-1 leading-tight mb-1">
                        {heading}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="h-3 w-3" />
                          {formatDateLabel(p.item_date)}
                        </span>
                        {time && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {time}
                          </span>
                        )}
                        {location && (
                          <span className="inline-flex items-center gap-1 min-w-0">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{location}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center">
            <p className="text-sm text-foreground font-medium mb-1">
              Nothing coming up yet
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Be the first to organise a playdate or mark yourself going to an activity.
            </p>
            <Button
              size="sm"
              onClick={() =>
                navigate(user ? "/activities" : "/auth?returnUrl=/activities")
              }
            >
              Browse activities
            </Button>
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <Button
            size="lg"
            onClick={() => navigate("/playdates")}
            className="w-full sm:w-auto px-8 py-6 text-base font-semibold shadow-md hover:shadow-lg transition-all"
          >
            View All Playdates
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default MeetLocalsSection;
